/**
 * Vue 2 Theme Provider Plugin
 * 3 Farklı Ürün Teması: İcra Pro, Ofis Pro, E-İcra Pro
 */

// Tema konfigürasyonları
export const THEMES = {
  icrapro: {
    name: 'İcra Pro',
    color: '#5BA3E7',
    description: 'İcra takip yönetimi',
    vuetifyTheme: {
      primary: '#5BA3E7',
      secondary: '#5F6D7E',
      accent: '#E3F2FD',
      error: '#F45150',
      warning: '#FFA826',
      info: '#00BBD4',
      success: '#33C481',
    }
  },
  ofispro: {
    name: 'Ofis Pro',
    color: '#9D7AE8',
    description: 'Ofis yönetimi',
    vuetifyTheme: {
      primary: '#9D7AE8',
      secondary: '#5F6D7E',
      accent: '#F3E5F5',
      error: '#F45150',
      warning: '#FFA826',
      info: '#00BBD4',
      success: '#33C481',
    }
  },
  eicrapro: {
    name: 'E-İcra Pro',
    color: '#FF9500',
    description: 'Elektronik icra takip',
    vuetifyTheme: {
      primary: '#FF9500',
      secondary: '#5F6D7E',
      accent: '#FFF4E6',
      error: '#F45150',
      warning: '#FFA826',
      info: '#00BBD4',
      success: '#33C481',
    }
  }
};

// Default tema
const DEFAULT_THEME = 'eicrapro';

// LocalStorage key
const STORAGE_KEY = 'app-theme';

/**
 * Tema uygulama fonksiyonu
 */
function applyTheme(themeName, vuetify) {
  // Geçerli tema kontrolü
  if (!THEMES[themeName]) {
    console.warn(`Tema bulunamadı: ${themeName}. Default tema uygulanıyor.`);
    themeName = DEFAULT_THEME;
  }

  // HTML root elementine data-theme attribute'u ekle
  document.documentElement.setAttribute('data-theme', themeName);

  // LocalStorage'a kaydet
  try {
    localStorage.setItem(STORAGE_KEY, themeName);
  } catch (e) {
    console.warn('LocalStorage tema kaydedilemedi:', e);
  }

  // Vuetify temasını güncelle
  if (vuetify) {
    const themeConfig = THEMES[themeName].vuetifyTheme;
    Object.keys(themeConfig).forEach(key => {
      vuetify.theme.themes.light[key] = themeConfig[key];
    });
  }

  return themeName;
}

/**
 * Kaydedilmiş temayı yükle
 */
function loadSavedTheme() {
  try {
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    return savedTheme && THEMES[savedTheme] ? savedTheme : DEFAULT_THEME;
  } catch (e) {
    console.warn('LocalStorage tema okunamadı:', e);
    return DEFAULT_THEME;
  }
}

/**
 * Mevcut temayı al
 */
function getCurrentTheme() {
  return document.documentElement.getAttribute('data-theme') || DEFAULT_THEME;
}

/**
 * Tema bilgilerini al
 */
function getThemeInfo(themeName = null) {
  const theme = themeName || getCurrentTheme();
  return THEMES[theme] || THEMES[DEFAULT_THEME];
}

/**
 * Tüm temaları listele
 */
function getAvailableThemes() {
  return Object.keys(THEMES).map(key => ({
    key,
    ...THEMES[key]
  }));
}

/**
 * Vue Plugin
 */
export default {
  install(Vue, options = {}) {
    const defaultTheme = options.defaultTheme || loadSavedTheme();

    // Vue prototype'a tema metodları ekle
    Vue.prototype.$theme = {
      current: defaultTheme,
      apply: applyTheme,
      getCurrent: getCurrentTheme,
      getInfo: getThemeInfo,
      getAvailable: getAvailableThemes,
      THEMES,
    };

    // Global mixin - Her component'te reaktif tema desteği
    Vue.mixin({
      data() {
        return {
          $currentTheme: defaultTheme
        };
      },

      created() {
        // Tema değişikliğini dinle
        this._themeObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
              this.$currentTheme = getCurrentTheme();
              this.$emit('theme-changed', this.$currentTheme);
            }
          });
        });

        this._themeObserver.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['data-theme']
        });
      },

      beforeDestroy() {
        // Cleanup
        if (this._themeObserver) {
          this._themeObserver.disconnect();
        }
      },

      methods: {
        /**
         * Tema değiştir
         */
        $changeTheme(themeName) {
          const vuetify = this.$vuetify;
          const newTheme = applyTheme(themeName, vuetify);
          this.$currentTheme = newTheme;

          // Event emit et
          this.$root.$emit('theme-changed', newTheme);

          return newTheme;
        },

        /**
         * Tema bilgisi al
         */
        $getThemeInfo(themeName = null) {
          return getThemeInfo(themeName);
        },

        /**
         * Aktif tema kontrolü
         */
        $isThemeActive(themeName) {
          return this.$currentTheme === themeName;
        }
      },

      computed: {
        /**
         * Mevcut tema bilgisi (reactive)
         */
        $themeInfo() {
          return getThemeInfo(this.$currentTheme);
        },

        /**
         * Mevcut tema rengi (reactive)
         */
        $themeColor() {
          return this.$themeInfo.color;
        }
      }
    });

    // İlk yüklemede temayı uygula
    Vue.nextTick(() => {
      applyTheme(defaultTheme, options.vuetify);
    });
  }
};

// Export utility fonksiyonları
export {
  applyTheme,
  loadSavedTheme,
  getCurrentTheme,
  getThemeInfo,
  getAvailableThemes,
  DEFAULT_THEME,
};
